import axios from 'axios';

/** minionsAPI PORT 기본값 — `.env` 의 VITE_MINIONS_API_BASE 로 덮어씀 */
const DEFAULT_FILE_API_BASE = 'http://127.0.0.1:3020';

declare global {
  interface Window {
    /** iframe·임베드: `app.js` 보다 먼저 실행되는 인라인 스크립트에서 설정 */
    __MINIONS_API_BASE__?: string;
  }
}

/**
 * 미설정 시 `/api` — Vite `server`·`preview` 가 동일 프록시로 minionsAPI 에 넘김 (`vite.shared.ts`).
 * file:// 에서는 `/api` 가 `file:///C:/api/...` 로 깨지므로 런타임에 절대 URL 로 폴백.
 * http(s) 로 열었는데 상위 사이트에 `/api` 프록시가 없으면 404 → `VITE_MINIONS_API_BASE` 또는 `__MINIONS_API_BASE__`.
 */
function resolveApiBaseURL(): string {
  if (typeof window !== 'undefined') {
    const injected = String(window.__MINIONS_API_BASE__ ?? '').trim();
    if (injected) return injected.replace(/\/$/, '');
  }
  const u = import.meta.env.VITE_MINIONS_API_BASE?.trim();
  if (u) return u.replace(/\/$/, '');
  if (typeof globalThis !== 'undefined' && globalThis.location?.protocol === 'file:') {
    return DEFAULT_FILE_API_BASE;
  }
  return '/api';
}

/** action=status 등과 동일한 기준 — `/health` 로 워처 가용 여부 확인 시 사용 */
export function getMinionsApiBaseURL(): string {
  return resolveApiBaseURL();
}

const client = axios.create({
  baseURL: resolveApiBaseURL(),
  timeout: 20000,
  headers: { Accept: 'application/json' },
});



export interface LadderStatus {

  ok?: boolean;

  state?: string;

  current_block?: number;

  next_block?: number | string;

  interval?: number;

  betting_remaining_seconds?: number;

  betting_allowed?: boolean;

  cycle_position?: number;

  [k: string]: unknown;

}



/** PHP JSON 이 숫자/문자 혼용일 때 대비 */

export function parseBettingRemainingSeconds(v: unknown): number | null {

  if (v == null || v === '') return null;

  const n = typeof v === 'number' ? v : Number(v);

  if (!Number.isFinite(n)) return null;

  return Math.max(0, Math.floor(n));

}



export function parseBettingAllowed(v: unknown): boolean | undefined {

  if (v === undefined || v === null) return undefined;

  if (v === true || v === 1) return true;

  if (v === false || v === 0) return false;

  if (typeof v === 'string') {

    const s = v.trim().toLowerCase();

    if (s === '1' || s === 'true' || s === 'yes' || s === 'on') return true;

    if (s === '0' || s === 'false' || s === 'no' || s === 'off') return false;

  }

  return undefined;

}



/** PHP 가 interval 을 문자열로 보낼 때 */

export function parseIntervalSec(v: unknown): number | undefined {

  if (v == null || v === '') return undefined;

  const n = typeof v === 'number' ? v : Number(v);

  if (!Number.isFinite(n) || n < 8) return undefined;

  return Math.floor(n);

}



/** ok: 0 / "0" / false — ethminions 는 !data.ok 로 묶어서 처리 */

export function isStatusOkFalse(ok: unknown): boolean {

  if (ok === false || ok === 0) return true;

  if (ok === '0' || ok === 'false' || ok === 'FALSE') return true;

  return false;

}



export interface LadderResultPayload {

  ok?: boolean;

  block?: number;

  current_block?: number;

  hash?: string;

  randomnessHex?: string;

  randomness?: string;

  start?: string;

  lines?: number;

  result?: string;

  [k: string]: unknown;

}



/** action=status JSON 에서 전광판용 필드만 정규화 */

export function ladderStatusFromJsonRaw(raw: Record<string, unknown>): LadderStatus {

  const brs = parseBettingRemainingSeconds(

    raw.betting_remaining_seconds ?? raw.betting_remaining_sec

  );

  const allowed = parseBettingAllowed(raw.betting_allowed);

  const nextRaw = raw.next_block ?? raw.next_wait_block;

  let next_block: number | string | undefined;

  if (nextRaw != null && nextRaw !== '') {

    const nn = Number(nextRaw);

    next_block = Number.isFinite(nn) ? nn : String(nextRaw);

  }

  const out: LadderStatus = { ...(raw as LadderStatus) };

  if (brs !== null) out.betting_remaining_seconds = brs;

  else delete out.betting_remaining_seconds;

  if (allowed !== undefined) out.betting_allowed = allowed;

  else delete out.betting_allowed;

  const intv = parseIntervalSec(raw.interval);

  if (intv !== undefined) out.interval = intv;

  else delete out.interval;

  if (next_block !== undefined) out.next_block = next_block;

  if (isStatusOkFalse(raw.ok)) {

    out.ok = false;

  } else if (raw.ok === true || raw.ok === 1 || raw.ok === '1') {

    out.ok = true;

  }

  return out;

}



function nestedObjectToResultPayload(o: Record<string, unknown>): LadderResultPayload | null {

  const hex = o.randomnessHex ?? o.hash ?? o.randomness;

  if (hex == null || String(hex).trim() === '') return null;

  const b = o.current_block ?? o.block;

  const blockNum = b != null && String(b).trim() !== '' ? Number(b) : NaN;

  return {

    ok: true,

    current_block: Number.isFinite(blockNum) ? blockNum : undefined,

    block: Number.isFinite(blockNum) ? blockNum : undefined,

    randomnessHex: String(hex),

    start: typeof o.start === 'string' ? o.start : undefined,

    lines: typeof o.lines === 'number' ? o.lines : undefined,

    result: typeof o.result === 'string' ? o.result : undefined,

  };

}



/**

 * PHP 가 action=status&include_latest=1 응답에 넣는 “직전 확정 회차”.

 * 서버 미구현 시 항상 null (뷰는 WS 또는 추후 PHP 보강으로만 결과 수신).

 */

export function parseEmbeddedLatestFromStatusRaw(raw: Record<string, unknown>): LadderResultPayload | null {

  const nestedKeys = ['latest_round', 'latest_result', 'last_round', 'embedded_result', 'round'];

  for (const k of nestedKeys) {

    const v = raw[k];

    if (v && typeof v === 'object' && !Array.isArray(v)) {

      const p = nestedObjectToResultPayload(v as Record<string, unknown>);

      if (p) return p;

    }

  }

  const lb = raw.latest_block ?? raw.latestBlock;

  if (lb != null && String(lb).trim() !== '') {

    const hex =

      raw.latest_randomness_hex ?? raw.latest_randomness ?? raw.latest_hash ?? raw.latestHash;

    if (hex != null && String(hex).trim() !== '') {

      const block = Number(lb);

      return {

        ok: true,

        current_block: Number.isFinite(block) ? block : undefined,

        block: Number.isFinite(block) ? block : undefined,

        randomnessHex: String(hex),

        start: typeof raw.latest_start === 'string' ? raw.latest_start : undefined,

        lines: typeof raw.latest_lines === 'number' ? raw.latest_lines : undefined,

        result: typeof raw.latest_result_parity === 'string' ? raw.latest_result_parity : undefined,

      };

    }

  }

  return null;

}



/**

 * 한 번의 GET 으로 status + (선택) 직전 회차.

 * URL: /api/minions/ladder?action=status&include_latest=1

 * PHP 가 include_latest 를 무시하면 embeddedResult 는 null — 기존과 동일.

 */

export async function fetchLadderStatusWithLatest(): Promise<{

  status: LadderStatus | null;

  embeddedResult: LadderResultPayload | null;

}> {

  try {

    const { data } = await client.get('/minions/ladder', {

      params: { action: 'status', include_latest: 1 },

      validateStatus: () => true,

    });

    if (!data || typeof data !== 'object' || Array.isArray(data)) {

      return { status: null, embeddedResult: null };

    }

    const raw = data as Record<string, unknown>;

    const embeddedResult = parseEmbeddedLatestFromStatusRaw(raw);

    const status = ladderStatusFromJsonRaw(raw);

    return { status, embeddedResult };

  } catch {

    return { status: null, embeddedResult: null };

  }

}



export async function fetchLadderStatus(): Promise<LadderStatus | null> {

  const { status } = await fetchLadderStatusWithLatest();

  return status;

}



export async function fetchHistory(limit = 4000): Promise<unknown[] | null> {
  try {
    const { data } = await client.get('/minions/ladder', {
      params: { action: 'history', limit },
      validateStatus: () => true,
    });
    if (data == null) return null;
    if (Array.isArray(data)) return data;
    if (typeof data !== 'object') return null;
    const o = data as Record<string, unknown>;
    const h = o.history ?? o.data ?? o.results ?? o.items ?? o.list;
    if (Array.isArray(h)) return h;
    return null;
  } catch {
    return null;
  }
}


